import * as React from 'react';
import { styled } from '@mui/material/styles';
import {
    Button,
    CardActions,
    CardContent,
    CircularProgress,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Box,
    TextField,
    Typography,
    Link as MuiLink,
} from '@mui/material';
import { Form, useLogin, useNotify, useSafeSetState } from 'ra-core';
import { Login, TextInput } from 'react-admin';
import { SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ForgotPasswordPage } from 'ra-supabase';
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox';
import { OrDivider, AuthButton, LogoHeader } from './components';
import { supabase } from '../providers/supabase/supabase';

type FieldValues = Record<string, any>;

type SiteStatusRow = {
    value: string;
    message: string | null;
    code: string | null;
};

const PREFIX = 'RaLoginForm';

export const LoginFormClasses = {
    content: `${PREFIX}-content`,
    button: `${PREFIX}-button`,
    icon: `${PREFIX}-icon`,
};

const StyledForm = styled(Form, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    [`& .${LoginFormClasses.content}`]: {
        width: 300,
    },
    [`& .${LoginFormClasses.button}`]: {
        marginTop: theme.spacing(2),
    },
    [`& .${LoginFormClasses.icon}`]: {
        margin: theme.spacing(0.3),
    },
}));

const REQUIRES_CODE_STATUSES = ['System Update', 'Beta'] as const;

export const LoginForm = () => {
    const [loading, setLoading] = useSafeSetState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const [siteStatus, setSiteStatus] = React.useState<SiteStatusRow | null>(
        null
    );
    const [codeDialogOpen, setCodeDialogOpen] = React.useState(false);
    const [pendingCredentials, setPendingCredentials] =
        React.useState<FieldValues | null>(null);
    const [codeInput, setCodeInput] = React.useState('');
    const [codeSubmitting, setCodeSubmitting] = useSafeSetState(false);
    const login = useLogin();
    const notify = useNotify();

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data, error } = await Promise.resolve(
                    supabase
                        .from('site_status')
                        .select('value, message, code')
                        .eq('key', 'current')
                        .single()
                );
                if (!cancelled && !error && data) {
                    setSiteStatus({
                        value: data.value ?? 'Live',
                        message: data.message ?? null,
                        code: data.code ?? null,
                    });
                }
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const doLogin = React.useCallback(
        (values: FieldValues): Promise<void> => {
            setLoading(true);
            return login(values)
                .then(() => setLoading(false))
                .catch(error => {
                    setLoading(false);
                    notify(
                        typeof error === 'string'
                            ? error
                            : typeof error === 'undefined' || !error.message
                              ? 'ra.auth.sign_in_error'
                              : error.message,
                        { type: 'error' }
                    );
                    throw error;
                });
        },
        [login, notify]
    );

    const submit: SubmitHandler<FieldValues> = values => {
        if (!termsAccepted) {
            notify('You must accept the terms and conditions to log in.', {
                type: 'warning',
            });
            return;
        }
        const status = siteStatus?.value ?? 'Live';
        if (REQUIRES_CODE_STATUSES.includes(status as any)) {
            const requiredCode = (siteStatus?.code ?? '').trim();
            if (!requiredCode) {
                doLogin(values);
                return;
            }
            setPendingCredentials(values);
            setCodeInput('');
            setCodeDialogOpen(true);
            return;
        }
        doLogin(values);
    };

    const handleCodeDialogSubmit = () => {
        const requiredCode = (siteStatus?.code ?? '').trim();
        const entered = codeInput.trim();
        if (requiredCode && entered !== requiredCode) {
            notify('The access code is incorrect. Please try again.', {
                type: 'error',
            });
            return;
        }
        if (!pendingCredentials) return;
        setCodeSubmitting(true);
        doLogin(pendingCredentials)
            .then(() => {
                setCodeDialogOpen(false);
                setCodeInput('');
                setPendingCredentials(null);
            })
            .catch(() => {})
            .finally(() => setCodeSubmitting(false));
    };

    return (
        <Login>
            <LogoHeader />
            <StyledForm onSubmit={submit} mode="onChange" noValidate>
                <CardContent className={LoginFormClasses.content}>
                    <TextInput
                        autoFocus
                        source="email"
                        label="Email"
                        autoComplete="email"
                    />
                    <TextInput
                        source="password"
                        label="Password"
                        type="password"
                        autoComplete="current-password"
                    />
                </CardContent>
                <CardActions
                    sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}
                >
                    <TermsAndConditionsCheckbox
                        checked={termsAccepted}
                        onChange={setTermsAccepted}
                        disabled={loading}
                        showRequiredMessage={true}
                        sx={{ alignSelf: 'flex-start' }}
                    />

                    <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={loading || !termsAccepted}
                        fullWidth
                        className={LoginFormClasses.button}
                    >
                        {loading ? (
                            <CircularProgress
                                className={LoginFormClasses.icon}
                                size={19}
                                thickness={3}
                            />
                        ) : (
                            'Sign In'
                        )}
                    </Button>

                    <OrDivider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%',
                        }}
                    >
                        <AuthButton
                            component={Link}
                            to={ForgotPasswordPage.path}
                        >
                            Forgot your password?
                        </AuthButton>

                        <AuthButton component={Link} to="/register">
                            Register
                        </AuthButton>
                    </Box>
                </CardActions>
            </StyledForm>

            <Dialog
                open={codeDialogOpen}
                onClose={() => {}}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Access code required</DialogTitle>
                <DialogContent>
                    {siteStatus?.message && (
                        <Typography sx={{ mb: 2 }} color="text.secondary">
                            {siteStatus.message}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Enter the access code to sign in.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Access code"
                        value={codeInput}
                        onChange={e => setCodeInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCodeDialogSubmit();
                            }
                        }}
                        disabled={codeSubmitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCodeDialogSubmit}
                        variant="contained"
                        color="primary"
                        disabled={codeSubmitting || !codeInput.trim()}
                    >
                        {codeSubmitting ? (
                            <CircularProgress size={20} />
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Login>
    );
};
